



























import { Vue, Component, Prop } from "vue-property-decorator";
import { mapState, mapGetters, mapMutations, mapActions } from "vuex";
import { Location } from "vue-router";
import useAuthentication from "@/composition/useAuthentication";
import { capitalize } from "@/filters";
import { isMobile } from "@/utils/responsive";
import CdkFilterList from "@/components/CdkFilterList/CdkFilterList.vue";
import CdkNavigation from "./CdkNavigation.vue";
import CdkNavigationItem from "./CdkNavigationItem.vue";
import CdkSettingsFilterList from "@/components/CdkFilterList/CdkSettingsFilterList.vue";
import SlcShortcutsProvider from "@/components/SlcShortcuts/SlcShortcutsProvider.vue";
import useSharedSearch from "@/composition/useSharedSearch";
import store from "@/store";

@Component({
  // @ts-ignore
  setup() {
    const { validateAndRetrieveToken } = useAuthentication();
    return { token: validateAndRetrieveToken };
  },
  components: {
    CdkNavigation,
    CdkNavigationItem,
    Feed: CdkFilterList,
    Settings: CdkSettingsFilterList,
    SlcShortcutsProvider
  },
  computed: {
    ...mapState("ui", ["left", "leftWidth"]),
    ...mapState("stageconfig", ["config"]),
    ...mapGetters("filters", [
      "getFiltersSplitByOwner",
      "getNextVisibleFilterById",
      "hasFilters"
    ])
  },
  methods: {
    ...mapMutations("ui", ["setLeft"]),
    ...mapActions("filters", ["updateFilterIsVisible"])
  }
})
export default class CdkNavigationProvider extends Vue {
  @Prop(String) view!: string;

  @Prop({
    type: Boolean,
    default: true
  })
  showActions!: boolean;

  left: boolean;

  setLeft: Function;

  getFiltersSplitByOwner: any[];

  updateFilterIsVisible: any;

  hasFilters: boolean;

  config: any;

  token: Function;

  leftWidth: string;

  setFilterVisibility({ id, isVisible }) {
    this.updateFilterIsVisible({
      id,
      isVisible,
      authJWT: this.token()
    });
  }

  getFilterSettingsItems(filter) {
    return [
      ...(filter.isPublic === true
        ? [
            {
              anchor: "description",
              label: this.$t("Information")
            }
          ]
        : []),
      ...(filter.isPublic !== true
        ? [
            {
              anchor: "settings",
              label: this.$t("Preferences")
            }
          ]
        : []),
      {
        anchor: "visibility",
        label: this.$t("showFilter")
      },
      {
        anchor: "notifications",
        label: this.$t("notifications")
      },
      ...(filter.isPublic !== true
        ? [
            {
              anchor: "remove",
              label: this.$t("removeFilter")
            }
          ]
        : [])
    ];
  }

  /**
   * Updates the router with a new filter and scroll hash, if given
   * @param {Object} options
   * @param {String} options.filterId
   * @param {String} [options.hash=""]
   */
  switchFilter({ filterId, hash = "" }) {
    this.$router.push({
      path: "/settings",
      query: {
        filterId
      },
      ...(hash ? { hash } : {})
    });
  }

  /**
   * Returns either the list entry to preferences or to notification settings
   */
  get navigationItems(): any | null {
    if (this.view === "Settings") {
      return [
        ...(this.hasFilters
          ? [
              {
                route: { path: "/settings/notifications" },
                label: this.$t("notifications"),
                icon: "notifications"
              }
            ]
          : []),
        {
          route: { path: "/settings/language" },
          label: this.$t("language"),
          icon: "mdi-web"
        }
      ];
    }
    if (this.view === "Feed") {
      if (this.$vuetify.breakpoint.mdAndDown) {
        return [
          {
            route: { path: "/settings" },
            label: this.$t("Preferences"),
            icon: "settings",
            matomoEvent: {
              category: "Left Navigation Bar",
              action: "Click Settings",
              name: "Settings"
            }
          },
          {
            route: { path: "/help" },
            label: this.$t("help.label"),
            icon: "help_outline"
          }
        ];
      }
      return [
        {
          route: { path: "/settings" },
          label: this.$t("Preferences"),
          icon: "settings",
          matomoEvent: {
            category: "Left Navigation Bar",
            action: "Click Settings",
            name: "Settings"
          }
        }
      ];
    }
    return null;
  }

  get filtersByGroup() {
    const { filterId } = this.$route.query;
    const { saveSharedFilter, showDialog } = useSharedSearch();

    return this.getFiltersSplitByOwner.map(groups => [
      ...groups
        .filter(({ isVisible }) => (this.view === "Feed" ? isVisible : true))
        .map(item => ({
          ...item,
          group: capitalize(this.$t(`filter:${item.group}`)),
          to: this.to(item.id),
          tooltip: `${this.$t("Options for")} ${item.name}`,
          isCurrentFilter: filterId === item.id,
          actions: [
            {
              label: this.$t("Preferences"),
              onClick: () => {
                this.$router.push({
                  path: "/settings",
                  query: {
                    filterId: item.id
                  }
                });
              }
            },
            {
              label: this.$t("Hide"),
              onClick: () => {
                this.setFilterVisibility({
                  id: item.id,
                  isVisible: false
                });

                // NB: If user is currently view newsItems of this filter,
                // navigate out of the filter
                if (filterId === item.id) {
                  const { path, params, query } = this.$route;
                  const newQuery = { ...query };
                  delete newQuery.filterId;

                  this.$router.push({
                    path,
                    params,
                    query: newQuery
                  });
                }
              }
            },
            {
              label: this.$t("ShareSavedSearch"),
              onClick: () => {
                const savedSearch = store.getters["filters/getFilterById"](
                  item.id
                );
                saveSharedFilter(savedSearch, true);
                showDialog();
              }
            }
          ],
          ...(this.view === "Settings"
            ? {
                active: filterId === item.id,
                items: this.getFilterSettingsItems(item)
              }
            : {})
        }))
    ]);
  }

  to(id): Location {
    const { path, query, params } = this.$route;
    return {
      path,
      params,
      query: {
        ...query,
        filterId: id
      }
    };
  }

  async mounted() {
    await this.$nextTick();
    if (!isMobile(this)) {
      this.setLeft(true);
    }
  }
}
